import { type FC, useMemo } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { AutomationStepList } from "@/components/automations/AutomationStepList";
import { StepListContainer } from "@/routes/automations/automation-details-page/StepListContainer";
import {
    convertWorkflowActionToFormSteps,
    convertWorkflowActionToFormLimits,
    convertTriggersToFormData,
} from "@/components/automations/automation-edit-form-data";
import type { JsonValue } from "@bufbuild/protobuf";
import { CreateWorkflowRequest } from "gitpod-next-api/gitpod/v1/workflow_pb";
import yaml from "js-yaml";

type WorkflowPreviewProps = {
    content: string;
    className?: string;
};

/**
 * Renders a workflow YAML file as a rich preview using the same components
 * as the automation details page (AutomationStepList, triggers, limits).
 */
export const WorkflowPreview: FC<WorkflowPreviewProps> = ({ content, className }) => {
    const parsed = useMemo(() => {
        try {
            const raw = yaml.load(content);
            if (!raw || typeof raw !== "object") return null;

            const req = CreateWorkflowRequest.fromJson(raw as JsonValue);

            const steps = req.action ? convertWorkflowActionToFormSteps(req.action) : [];
            const trigger = req.triggers?.[0] ? convertTriggersToFormData(req.triggers)[0] : undefined;
            const limits = convertWorkflowActionToFormLimits(req.action);

            return {
                name: req.name || "Untitled Workflow",
                description: req.description || "",
                steps,
                trigger,
                limits,
            };
        } catch {
            return null;
        }
    }, [content]);

    if (!parsed) {
        return (
            <div className={cn("flex items-center justify-center rounded-lg border border-border-base p-6", className)}>
                <Text className="text-sm text-content-muted">Unable to parse workflow YAML</Text>
            </div>
        );
    }

    return (
        <div className={cn("rounded-lg border border-border-base bg-surface-secondary", className)}>
            {/* Header */}
            <div className="border-b border-border-base px-4 py-3">
                <Text className="text-base font-semibold text-content-primary">{parsed.name}</Text>
                {parsed.description && (
                    <Text className="mt-0.5 text-sm text-content-secondary">{parsed.description}</Text>
                )}
            </div>

            {/* Step list — same rendering as the automation details page */}
            {parsed.steps.length > 0 ? (
                <StepListContainer>
                    <div className="flex w-full max-w-screen-xl flex-row items-start justify-center gap-4">
                        <AutomationStepList
                            trigger={parsed.trigger}
                            limits={parsed.limits}
                            steps={parsed.steps}
                        />
                    </div>
                </StepListContainer>
            ) : (
                <div className="flex items-center justify-center p-6">
                    <Text className="text-sm text-content-muted">No steps defined</Text>
                </div>
            )}
        </div>
    );
};
